
import React, { useState, useEffect, useRef } from 'react';
import { FactProcess, ProcessStatus, DeclarationType } from '../types';
import VoiceInterview from './VoiceInterview';
import { generateDraftDeclaration } from '../services/geminiService';
import { CaseDatabase } from '../services/db';
import { ExportService } from '../services/exportService';
import { NotificationService } from '../services/notificationService';

const ProcessDashboard: React.FC = () => {
  const [processes, setProcesses] = useState<FactProcess[]>([]);
  const [activeIntake, setActiveIntake] = useState<FactProcess | null>(null);
  const [viewingTranscript, setViewingTranscript] = useState<FactProcess | null>(null);
  const [rescheduling, setRescheduling] = useState<FactProcess | null>(null);
  const [newDate, setNewDate] = useState('');
  const [logs, setLogs] = useState(NotificationService.getLogs());
  
  const [isDrafting, setIsDrafting] = useState(false);
  const [shareOptions, setShareOptions] = useState({ client: true, staff: true, transcript: true });
  const [loading, setLoading] = useState(true);
  const [liveAppointments, setLiveAppointments] = useState<FactProcess[]>([]);

  // Use a ref to keep track of processes without triggering effect loops
  const processesRef = useRef<FactProcess[]>([]);

  useEffect(() => {
    loadProcesses();
    const handleLogUpdate = () => setLogs(NotificationService.getLogs());
    window.addEventListener('activity_log_updated', handleLogUpdate);
    
    // Automation Monitor - Run checks every 5 seconds
    const interval = setInterval(() => {
      checkAutomationTriggers();
    }, 5000);

    return () => {
      window.removeEventListener('activity_log_updated', handleLogUpdate);
      clearInterval(interval);
    };
  }, []);

  const loadProcesses = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await CaseDatabase.getAll();
      setProcesses(data);
      processesRef.current = data;
    } catch (err) {
      console.error("Failed to load cases", err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const checkAutomationTriggers = async () => {
    const now = new Date();
    let updatedAny = false;
    const currentLive: FactProcess[] = [];
    const currentProcesses = processesRef.current;

    for (const p of currentProcesses) {
      const aptTime = new Date(p.appointment.dateTime);
      const diffMs = aptTime.getTime() - now.getTime();
      const diffMinutes = diffMs / (1000 * 60);

      // 24h Reminder
      if (diffMinutes <= 1440 && diffMinutes > 1430 && !p.notifications.reminder24hSent) {
        await NotificationService.triggerReminder(p, '24h');
        updatedAny = true;
      }

      // 15m Reminder
      if (diffMinutes <= 15 && diffMinutes > 14 && !p.notifications.reminder15mSent) {
        await NotificationService.triggerReminder(p, '15m');
        updatedAny = true;
      }

      // Live Check (Auto-start within 10 minutes of appointment time)
      const isDueNow = diffMinutes <= 0 && diffMinutes > -10;
      if (isDueNow && p.status === ProcessStatus.PENDING) {
        await NotificationService.triggerAutoStart(p);
        updatedAny = true;
      }

      if (isDueNow || p.status === ProcessStatus.IN_PROGRESS) {
        currentLive.push(p);
      }
    }

    setLiveAppointments(currentLive);
    // Silent refresh if automation updated something
    if (updatedAny) loadProcesses(true);
  };

  const handleUpdateIntake = async (updated: FactProcess) => {
    const next = processes.map(p => p.id === updated.id ? updated : p);
    setProcesses(next);
    processesRef.current = next;
    
    if (activeIntake?.id === updated.id) setActiveIntake(updated);
    if (viewingTranscript?.id === updated.id) setViewingTranscript(updated);

    try {
      await CaseDatabase.save(updated);
    } catch (err) {
      console.error("Failed to save update", err);
    }
  };

  const handleReschedule = async () => {
    if (rescheduling && newDate) {
      if (new Date(newDate) < new Date()) {
        alert("Cannot reschedule to a time in the past.");
        return;
      }

      await NotificationService.sendRescheduleNotification(rescheduling, newDate);
      
      const updated = {
        ...rescheduling,
        appointment: { ...rescheduling.appointment, dateTime: newDate },
        status: ProcessStatus.PENDING,
        notifications: { ...rescheduling.notifications, rescheduleSent: true },
        lastUpdated: new Date().toISOString()
      };
      
      handleUpdateIntake(updated);
      setRescheduling(null);
      setNewDate('');
      alert('Client notified and appointment updated.');
    }
  };

  const startInterview = (intake: FactProcess) => {
    setActiveIntake(intake);
  };

  const handleDraft = async (intake: FactProcess) => {
    setIsDrafting(true);
    const draft = await generateDraftDeclaration(intake);
    const updated = {
      ...intake,
      status: ProcessStatus.DRAFTED,
      draftedDeclaration: draft,
      lastUpdated: new Date().toISOString()
    };
    handleUpdateIntake(updated);
    setIsDrafting(false);
  };

  const copyTranscript = () => {
    if (!viewingTranscript) return;
    const text = viewingTranscript.transcript.map(t => `${t.role}: ${t.text}`).join('\n');
    navigator.clipboard.writeText(text);
    alert('Transcript copied to clipboard');
  };

  const getStatusBadge = (status: ProcessStatus) => {
    switch (status) {
      case ProcessStatus.PENDING: 
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-500 border border-amber-200 dark:border-amber-800/50">Pending</span>;
      case ProcessStatus.IN_PROGRESS: 
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-700/50 animate-pulse">Live Now</span>;
      case ProcessStatus.PAUSED: 
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">Paused</span>;
      case ProcessStatus.COMPLETED: 
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 border border-blue-200 dark:border-blue-800/50">Complete</span>;
      case ProcessStatus.DRAFTED: 
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400 border border-purple-200 dark:border-purple-800/50">Draft Ready</span>;
      default: return null;
    }
  };

  const StatCard = ({ title, count, icon, color }: { title: string, count: number, icon: string, color: string }) => (
    <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-100 dark:border-slate-800 hover:shadow-md transition-all">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider mb-2">{title}</p>
          <h3 className="text-4xl font-serif font-medium text-slate-800 dark:text-slate-100">{count}</h3>
        </div>
        <div className={`p-3 rounded-2xl ${color} bg-opacity-10 dark:bg-opacity-20`}>
          <i className={`fas ${icon} text-lg ${color.replace('bg-', 'text-')}`}></i>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8 pb-20">
      {/* Live Intercept Header */}
      {liveAppointments.length > 0 && (
        <div className="bg-emerald-600 dark:bg-emerald-500 rounded-[2rem] p-6 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 animate-in slide-in-from-top-4 duration-500">
          <div className="flex items-center gap-6">
             <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
                <i className="fas fa-phone-arrow-up-right text-2xl"></i>
             </div>
             <div>
                <h3 className="text-lg font-bold">Active Intake Session Detected</h3>
                <p className="text-emerald-50 text-sm opacity-90">
                  {liveAppointments.length} matter{liveAppointments.length > 1 ? 's' : ''} currently in active consultation.
                </p>
             </div>
          </div>
          <div className="flex gap-3">
             <button 
                onClick={() => startInterview(liveAppointments[0])}
                className="px-6 py-3 bg-white text-emerald-600 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-emerald-50 transition-all shadow-lg"
             >
                Monitor Live Session
             </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-3 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard title="Active Interviews" count={processes.filter(p => p.status === ProcessStatus.IN_PROGRESS).length} icon="fa-comments" color="bg-blue-600" />
            <StatCard title="Drafts for Review" count={processes.filter(p => p.status === ProcessStatus.DRAFTED).length} icon="fa-file-signature" color="bg-purple-600" />
            <StatCard title="Upcoming" count={processes.filter(p => p.status === ProcessStatus.PENDING).length} icon="fa-calendar-check" color="bg-amber-600" />
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-[0_2px_12px_rgba(0,0,0,0.06)] border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="px-8 py-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-900">
              <h3 className="font-serif text-xl font-bold text-slate-900 dark:text-slate-100">Intake Queue</h3>
              <div className="flex gap-3">
                 <button onClick={() => ExportService.downloadMatterLog(processes)} className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest hover:text-amber-700 dark:hover:text-amber-500 transition-colors flex items-center gap-1.5">
                   <i className="fas fa-download"></i> Export CSV
                 </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50/50 dark:bg-slate-950/50 border-b border-slate-200 dark:border-slate-800">
                  <tr>
                    <th className="px-8 py-4 text-[10px] font-bold text-slate-500 dark:text-slate-500 uppercase tracking-widest">Client</th>
                    <th className="px-8 py-4 text-[10px] font-bold text-slate-500 dark:text-slate-500 uppercase tracking-widest text-center">Protocol</th>
                    <th className="px-8 py-4 text-[10px] font-bold text-slate-500 dark:text-slate-500 uppercase tracking-widest">Appointment</th>
                    <th className="px-8 py-4 text-[10px] font-bold text-slate-500 dark:text-slate-500 uppercase tracking-widest">Status</th>
                    <th className="px-8 py-4 text-[10px] font-bold text-slate-500 dark:text-slate-500 uppercase tracking-widest text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {loading ? (
                    <tr><td colSpan={5} className="px-6 py-24 text-center text-slate-400"><i className="fas fa-circle-notch animate-spin text-3xl mb-4"></i><p className="text-[10px] font-bold uppercase tracking-widest">Accessing Secure Records...</p></td></tr>
                  ) : processes.length === 0 ? (
                    <tr><td colSpan={5} className="px-6 py-24 text-center text-slate-400 font-medium">No active matters. Initialize a new intake to begin.</td></tr>
                  ) : (
                    processes.map(item => (
                      <tr key={item.id} className={`hover:bg-slate-50 dark:hover:bg-slate-950 transition-colors group ${item.status === ProcessStatus.IN_PROGRESS ? 'bg-emerald-50/10 dark:bg-emerald-900/5' : ''}`}>
                        <td className="px-8 py-6">
                          <div className="font-serif font-bold text-slate-900 dark:text-slate-100 text-lg leading-none">{item.client.name}</div>
                          <div className="text-[11px] text-slate-500 font-medium mt-1.5">{item.type}</div>
                        </td>
                        <td className="px-8 py-6 text-center">
                          <div className="flex flex-col items-center gap-1.5">
                            <i className={`fas ${item.appointment.method === 'Telephonic' ? 'fa-phone-volume' : 'fa-comment-dots'} text-slate-300 dark:text-slate-700`}></i>
                            <span className="text-[8px] font-black uppercase text-slate-400 tracking-tighter">{item.appointment.method}</span>
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                            {new Date(item.appointment.dateTime).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                          <div className="flex gap-2 items-center mt-1 text-[11px] font-medium text-slate-400">
                             {new Date(item.appointment.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                             {item.notifications.reminder15mSent && <i className="fas fa-paper-plane text-emerald-500 text-[9px]" title="15m Reminder Sent"></i>}
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          {getStatusBadge(item.status)}
                        </td>
                        <td className="px-8 py-6 text-right">
                          <div className="flex items-center justify-end gap-3">
                            {item.status !== ProcessStatus.DRAFTED && (
                              <button onClick={() => startInterview(item)} className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${item.status === ProcessStatus.IN_PROGRESS ? 'bg-emerald-600 text-white shadow-lg' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 hover:bg-blue-100'}`} title="Start/Resume Interview">
                                <i className={`fas ${item.status === ProcessStatus.PAUSED ? 'fa-play' : 'fa-microphone-alt'}`}></i>
                              </button>
                            )}
                            <button onClick={() => setRescheduling(item)} className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 transition-all" title="Reschedule">
                              <i className="fas fa-calendar-alt"></i>
                            </button>
                            {(item.status === ProcessStatus.COMPLETED || item.status === ProcessStatus.DRAFTED) && (
                              <button onClick={() => handleDraft(item)} disabled={isDrafting} className="w-10 h-10 rounded-xl flex items-center justify-center text-amber-700 dark:text-amber-500 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 transition-all" title="Generate Draft">
                                <i className="fas fa-magic"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-200 dark:border-slate-800 p-6 shadow-sm h-full max-h-[700px] flex flex-col">
            <h4 className="text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
              Live Automation
            </h4>
            <div className="flex-1 overflow-y-auto space-y-4 pr-1 custom-scrollbar">
              {logs.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Standby. Monitoring triggers...</p>
              ) : (
                logs.map(log => (
                  <div key={log.id} className={`p-4 rounded-2xl border text-[11px] leading-relaxed transition-all ${log.msg.includes('[SYSTEM') ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-100 dark:border-emerald-900/30' : 'bg-slate-50 dark:bg-slate-950/50 border-slate-100 dark:border-slate-800'}`}>
                    <p className={`font-medium mb-1.5 ${log.msg.includes('[SYSTEM') ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-700 dark:text-slate-300'}`}>{log.msg}</p>
                    <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest">{new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {activeIntake && (
        <VoiceInterview intake={activeIntake} onUpdate={handleUpdateIntake} onClose={() => setActiveIntake(null)} />
      )}

      {rescheduling && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
           <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl p-10 max-w-md w-full border border-white/10">
              <h3 className="text-2xl font-serif font-bold text-slate-900 dark:text-slate-100 mb-2">Reschedule Matter</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-8 font-medium">Coordinate a new strategic review session for {rescheduling.client.name}.</p>
              
              <div className="mb-8">
                <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest mb-3 ml-1">Proposed Time Block</label>
                <input 
                  type="datetime-local" 
                  min={new Date().toISOString().slice(0, 16)}
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="w-full px-6 py-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:border-slate-900 dark:focus:border-amber-600 outline-none transition-all dark:scheme-dark font-semibold"
                />
              </div>

              <div className="flex gap-4">
                 <button onClick={() => setRescheduling(null)} className="flex-1 py-4 text-slate-400 dark:text-slate-500 font-bold uppercase text-[10px] tracking-widest hover:bg-slate-50 dark:hover:bg-slate-800 rounded-2xl transition-colors">Cancel</button>
                 <button onClick={handleReschedule} className="flex-1 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold uppercase text-[10px] tracking-widest rounded-2xl hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors shadow-xl">Notify & Set</button>
              </div>
           </div>
        </div>
      )}

      {viewingTranscript && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 w-full max-w-5xl max-h-[90vh] rounded-[3rem] shadow-2xl flex flex-col overflow-hidden border border-slate-200 dark:border-slate-800">
            <div className="px-10 py-8 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-900">
              <div>
                <h3 className="font-serif font-bold text-2xl text-slate-900 dark:text-slate-100">Consultation Protocol</h3>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-bold tracking-[0.2em] mt-2">MATTER: {viewingTranscript.client.name} • ID: {viewingTranscript.id.substring(0,8)}</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={copyTranscript} className="w-12 h-12 rounded-2xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 flex items-center justify-center text-slate-400 hover:text-slate-900 transition-colors">
                  <i className="fas fa-copy"></i>
                </button>
                <button onClick={() => ExportService.downloadTranscript(viewingTranscript)} className="w-12 h-12 rounded-2xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 flex items-center justify-center text-slate-400 hover:text-slate-900 transition-colors">
                   <i className="fas fa-download"></i>
                </button>
                <button onClick={() => setViewingTranscript(null)} className="w-12 h-12 rounded-2xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors ml-2">
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-10 space-y-10 bg-[#fafbfc] dark:bg-slate-950 transition-colors">
              {viewingTranscript.draftedDeclaration && (
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm rounded-[2rem] p-10 mb-8 relative">
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-amber-600"></div>
                  <div className="flex items-center justify-between mb-8">
                    <h4 className="font-serif font-bold text-xl text-slate-900 dark:text-slate-100">Drafted Narrative</h4>
                    <button onClick={() => ExportService.downloadDraft(viewingTranscript)} className="text-[10px] font-bold text-amber-700 dark:text-amber-500 uppercase tracking-widest hover:underline">
                      Download Markdown
                    </button>
                  </div>
                  <div className="prose prose-slate dark:prose-invert max-w-none font-serif text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed text-lg">
                    {viewingTranscript.draftedDeclaration}
                  </div>
                </div>
              )}

              <div>
                <h4 className="text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest mb-6 border-b border-slate-100 dark:border-slate-800 pb-3">
                  Verbatim Data Stream
                </h4>
                <div className="space-y-6">
                    {viewingTranscript.transcript.map((entry, idx) => (
                      <div key={idx} className="flex flex-col gap-2">
                        <div className="flex items-baseline justify-between">
                          <span className={`text-[10px] font-bold uppercase tracking-[0.15em] ${entry.role === 'AI' ? 'text-amber-700 dark:text-amber-500' : 'text-blue-700 dark:text-blue-400'}`}>
                            {entry.role === 'AI' ? 'Attorney Victoria' : viewingTranscript.client.name}
                          </span>
                          <span className="text-[9px] text-slate-300 dark:text-slate-700 font-mono font-bold">
                            {new Date(entry.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className={`p-6 rounded-[1.5rem] border text-base leading-relaxed ${entry.role === 'AI' ? 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-300 font-serif' : 'bg-slate-50 dark:bg-slate-950 border-slate-100 dark:border-slate-800 text-slate-800 dark:text-slate-200'}`}>
                          {entry.text}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessDashboard;
